/**
 * services/config/config.service.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The Settings surface and the shell's boot call.
 *
 * SERVICE-LAYER CONTRACT — every service in this app follows it:
 *   - It owns the HTTP call, the URL (from api/endpoints.ts) and the response
 *     parse (from schemas/*). Nothing else does.
 *   - It THROWS on failure. It never swallows an error and never toasts —
 *     screens catch and call utils/errorHandler `handleApiError`, so the
 *     backend's own message reaches the user unmodified.
 *   - It returns domain data, not an axios response. A screen must not need to
 *     know that `.data` exists.
 * ──────────────────────────────────────────────────────────────────────────
 */
import axiosInstance from '../../api/axiosInstance';
import {
  Cash_Approval_Rule_Url,
  Cash_Approval_Rules_Url,
  Cash_Bootstrap_Url,
  Cash_Categories_Url,
  Cash_Category_Url,
  Cash_Config_Audit_Url,
  Cash_Option_Url,
  Cash_Options_Url,
  Cash_Policy_Url,
  Cash_Rules_Url,
} from '../../api/endpoints';
import { parseResponse } from '../../schemas/common';
import {
  approvalRulesResponseSchema,
  bootstrapSchema,
  categoriesResponseSchema,
  categorySchema,
  optionsResponseSchema,
  optionSchema,
  policyPatchResultSchema,
  policyResponseSchema,
  rulesSchema,
  type ApprovalRule,
  type ApprovalRuleCreate,
  type Bootstrap,
  type Category,
  type CategoryCreate,
  type OptionCreate,
  type OptionRow,
  type PolicyPatch,
  type PolicyPatchResult,
  type PolicyResponse,
  type Rules,
  type RulesPatch,
} from '../../schemas/config';

/**
 * The ONE call the shell makes on load: which console, which gates, and the
 * whole configuration surface. Everything the app needs before it can render.
 */
export async function fetchBootstrap(): Promise<Bootstrap> {
  const { data } = await axiosInstance.get(Cash_Bootstrap_Url);
  return parseResponse(bootstrapSchema, data, 'GET /v1/config/bootstrap');
}

/* ── Option lists ────────────────────────────────────────────────────────── */

export async function fetchOptions(includeInactive = false): Promise<OptionRow[]> {
  const { data } = await axiosInstance.get(Cash_Options_Url, {
    params: includeInactive ? { include_inactive: 'true' } : undefined,
  });
  return parseResponse(optionsResponseSchema, data, 'GET /v1/config/options').options;
}

export async function addOption(input: OptionCreate): Promise<OptionRow> {
  const { data } = await axiosInstance.post(Cash_Options_Url, input);
  return parseResponse(optionSchema, data, 'POST /v1/config/options');
}

/**
 * Removes a value from the PICKER. Every record that already carries it keeps
 * it (invariant 6 — reference data deactivates, never deletes), and the API
 * refuses to remove the last value in a list.
 */
export async function deactivateOption(id: string): Promise<void> {
  await axiosInstance.delete(Cash_Option_Url(id));
}

/* ── Spend categories ────────────────────────────────────────────────────── */

export async function fetchCategories(): Promise<Category[]> {
  const { data } = await axiosInstance.get(Cash_Categories_Url);
  return parseResponse(categoriesResponseSchema, data, 'GET /v1/config/categories').categories;
}

export async function createCategory(input: CategoryCreate): Promise<Category> {
  const { data } = await axiosInstance.post(Cash_Categories_Url, input);
  return parseResponse(categorySchema, data, 'POST /v1/config/categories');
}

/** Deactivate, never delete — a vanished category would orphan its spends. */
export async function deactivateCategory(id: string): Promise<void> {
  await axiosInstance.delete(Cash_Category_Url(id));
}

/* ── Policy and rules ────────────────────────────────────────────────────── */

/** The policy plus the gate map — the API wraps both together. */
export async function fetchPolicy(): Promise<PolicyResponse> {
  const { data } = await axiosInstance.get(Cash_Policy_Url);
  return parseResponse(policyResponseSchema, data, 'GET /v1/config/policy');
}

/**
 * Send only what changed. Saving `threshold_minor` or `dual_over_minor` SEEDS
 * approval rules server-side rather than being evaluated as hidden thresholds,
 * so the Approval rules card will have new rows afterwards — callers refresh it.
 */
export async function patchPolicy(patch: PolicyPatch): Promise<PolicyPatchResult> {
  const { data } = await axiosInstance.patch(Cash_Policy_Url, patch);
  return parseResponse(policyPatchResultSchema, data, 'PATCH /v1/config/policy');
}

export async function fetchRules(): Promise<Rules> {
  const { data } = await axiosInstance.get(Cash_Rules_Url);
  return parseResponse(rulesSchema, data, 'GET /v1/config/rules');
}

export async function patchRules(patch: RulesPatch): Promise<Rules> {
  const { data } = await axiosInstance.patch(Cash_Rules_Url, patch);
  return parseResponse(rulesSchema, data, 'PATCH /v1/config/rules');
}

/* ── Approval rules ──────────────────────────────────────────────────────── */

export async function fetchApprovalRules(includeInactive = false): Promise<ApprovalRule[]> {
  const { data } = await axiosInstance.get(Cash_Approval_Rules_Url, {
    params: includeInactive ? { include_inactive: 'true' } : undefined,
  });
  return parseResponse(approvalRulesResponseSchema, data, 'GET /v1/config/approval-rules').rules;
}

export async function createApprovalRule(input: ApprovalRuleCreate): Promise<unknown> {
  const { data } = await axiosInstance.post(Cash_Approval_Rules_Url, input);
  return data;
}

/**
 * Pausing or removing a rule leaves items already waiting untouched: they keep
 * the reason they were held for, so an approver never sees a queue entry whose
 * justification has evaporated.
 */
export async function deactivateApprovalRule(id: string): Promise<void> {
  await axiosInstance.delete(Cash_Approval_Rule_Url(id));
}

/* ── Audit ───────────────────────────────────────────────────────────────── */

/** Who changed which setting, and when. Read-only. */
export async function fetchConfigAudit(): Promise<unknown> {
  const { data } = await axiosInstance.get(Cash_Config_Audit_Url);
  return data;
}
